import React, { memo, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Form, useFormikContext } from 'formik';
import { observer } from 'mobx-react';
import Grid from '@material-ui/core/Grid';
import {
  vehicleEquipmentStatusCode,
  noUMPDStates,
  policyLevelUMPDStates,
  minimumDeductibleCollision,
  minimumDeductibleComprehensive,
  isApplicantAnnualMilesEnabled,
  lookupsJson
} from '@ourbranch/lookups';
import { getSnapshot } from 'mobx-state-tree';
import { getTodayStr } from '@ourbranch/date-fns-helpers';

import { useRouteHelper } from 'core/hooks/use-route-helper';
import useErrors from 'common/hooks/use-errors';
import useOptions from 'core/hooks/use-options';
import { useStore } from 'core/store';
import { tooltipHoverTexts, lockedValuesTooltipTexts } from 'core/helpers/constants';
import { getBrandOptions } from 'core/helpers/lookups';
import { appConfig } from 'brand-config';
import useSession from 'core/hooks/use-session';
import Field from 'core/components/form/form.v2';
import withDatePicker from 'core/components/with-date-picker';
import { LabelTooltip } from 'core/components/label-tooltip';
import { Tooltip } from 'core/components/tooltip';
import icLocked from 'core/assets/svg/icLocked.svg';
import { Label } from 'core/components/label';
import CollapsibleCard from 'core/components/collapsible-card';
import { getMigratedArrivedValue, holdsMigratedCarryOverValue } from 'common/helpers/migrated-carry-over';
import { getDeductibleOptions, getCarNotification } from './helpers';
import { AddCarDetails } from './add-car-details';
import { GarageLocation } from './garage-location';
import { ConfirmRemoveCarModal } from './confirm-remove-car-modal';
import { verifiedMilesScrollTargetId } from './cars.constants';
import useStyles from './car.styles';

const deductibleComprehensiveLabels = {
  VA: 'OTHER THAN COLLISION DEDUCTIBLE'
};
const limitRentalLabels = {
  VA: 'TRANSPORTATION EXPENSES (RENTAL)'
};
const roadsideLabels = {
  VA: 'TOWING COVERAGE'
};

const acpeLabels = {
  VA: 'ADD-ON EQUIPMENT'
};

// Joint Ownership / PA 1200 (#35809): Yes/No selects keep the answers explicitly boolean, so "No"
// is distinguishable from unanswered — the rater only trusts the pair when both were answered.
const YES_NO_OPTIONS = [
  { id: true, value: 'Yes' },
  { id: false, value: 'No' }
];

// relationshipToInsured → the explicit same-household / related answers, used to prefill the panel
// when the joint owner is picked from the policy's drivers (staff can still correct them).
const RELATIONSHIP_TO_JOINT_OWNER_ANSWERS = {
  SP: { sameHousehold: true, related: true },
  RR: { sameHousehold: true, related: true },
  RNR: { sameHousehold: true, related: false },
  NRR: { sameHousehold: false, related: true },
  NRNR: { sameHousehold: false, related: false }
};

export const requiredCarFields = [
  'deductibleCollision',
  'deductibleComprehensive',
  'limitRental',
  'limitUMPD',
  'limitACPE'
];

const getCarHeader = (item) => {
  const header = `${item.year || ''} ${item.make || ''} `.concat(
    item.year || item.model || item.make ? ` ${item.model || ''}` : ` ${item.model || 'New Car'}`
  );
  return header;
};

// Helper to prioritize glass deductibles at the top of comprehensive options
const prioritizeGlassDeductibles = (options) => {
  const none = [];
  const glassOptions = [];
  const regularOptions = [];
  options.forEach((option) => {
    if (option.id === 'NONE') {
      none.push(option);
    } else if (option.id && (option.id.includes('/0GL') || option.id.includes('/50GL'))) {
      glassOptions.push(option);
    } else {
      regularOptions.push(option);
    }
  });
  return [...none, ...glassOptions, ...regularOptions];
};

export const getCoveragesOptions = ({
  state,
  car,
  policy,
  allDeductibleCollisionOptions,
  allDeductibleComprehensiveOptions,
  featureFlags
}) => {
  let comprehensiveOptions = allDeductibleComprehensiveOptions;

  // https://github.com/gobranch/branch/issues/18296
  if (state === 'KY' && (!policy || policy.rateControlDate > '2023-11-16')) {
    comprehensiveOptions = comprehensiveOptions.filter((option) => option.id === 'NONE' || option.id.includes('/0GL'));
  }

  // Prioritize glass deductibles at the top
  comprehensiveOptions = prioritizeGlassDeductibles(comprehensiveOptions);

  // A brand that files a lower comp/coll deductible than Branch's $500 floor sets its own (#36152).
  const brandMinimumDeductible = featureFlags?.minimumAutoPhysicalDamageDeductible;

  const deductibleCollisionOptions = getDeductibleOptions(
    allDeductibleCollisionOptions,
    state,
    car?.deductibleCollision,
    brandMinimumDeductible ?? minimumDeductibleCollision
  );

  const deductibleComprehensiveOptions = getDeductibleOptions(
    comprehensiveOptions,
    state,
    car?.deductibleComprehensive,
    brandMinimumDeductible ?? minimumDeductibleComprehensive
  );

  return {
    deductibleCollisionOptions,
    deductibleComprehensiveOptions
  };
};

const Car = observer(
  ({
    fieldName,
    item = {},
    index,
    removeFromList,
    onRemove,
    disabled,
    fromPolicy,
    openSignalOnError = 0,
    hideRemove = false,
    section = 'all'
  }) => {
    const classes = useStyles();
    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const {
      offer: offerStore,
      account: {
        policies: { policy: policyStore }
      }
    } = useStore();
    const routesContext = useRouteHelper();

    const errorsNode =
      (routesContext.inPolicyRoute && policyStore?.errors && !policyStore.loading && getSnapshot(policyStore.errors)) ||
      (routesContext.inOfferEditRoute && offerStore?.errors && !offerStore.loading && getSnapshot(offerStore.errors)) ||
      undefined;
    const errorsAndRejections = useErrors(errorsNode, (code) => code >= 4003);

    const car =
      (fromPolicy ? policyStore?.policy?.policyDetails?.cars[index] : offerStore?.offer?.quote?.cars[index]) || {};
    const displayLeaseLoan = fromPolicy ? policyStore?.showLeaseLoan : offerStore?.showLeaseLoan;

    const { setTouched, errors, setFieldValue, initialValues, values } = useFormikContext();
    const limitUMPDOptions = useOptions(
      'limitUMPD',
      policyStore?.policy?.state || values?.correctedAddress?.state,
      values?.cars?.[index]?.limitUMPD
    );

    // https://github.com/gobranch/branch/issues/20927
    // only show the rideshare option if they already have it on
    const initialCar = initialValues?.cars?.[index];
    const showRideShare = initialCar?.rideSharing;

    const { canAddCarsManually } = useSession();

    const state = item?.garageLocation?.state || null;

    const ratingState = fromPolicy
      ? policyStore?.policy?.state
      : offerStore?.offer?.quote?.correctedAddress?.state || values?.correctedAddress?.state;
    const showVerifiedMiles = isApplicantAnnualMilesEnabled(ratingState, getTodayStr(ratingState));
    // Carfax's figure always wins; a 0 from Carfax rates as Unknown, so it counts as no figure
    const hasCarfaxAnnualMiles = (car?.verifiedAnnualMilesDriven ?? item?.verifiedAnnualMilesDriven) > 0;
    const lockVerifiedMiles = fromPolicy || hasCarfaxAnnualMiles;
    const lockedMilesField = hasCarfaxAnnualMiles ? 'verifiedAnnualMilesDriven' : 'reportedAnnualMiles';
    const scrollTarget = offerStore?.scrollTo;
    const [openSignalFromNotice, setOpenSignalFromNotice] = useState(0);
    useEffect(() => {
      if (fromPolicy || scrollTarget !== verifiedMilesScrollTargetId(index)) {
        return undefined;
      }
      setOpenSignalFromNotice((signal) => signal + 1);
      const focusTimer = setTimeout(() => {
        document.getElementById(`${fieldName}.reportedAnnualMiles`)?.focus({ preventScroll: true });
      }, 150);
      return () => clearTimeout(focusTimer);
    }, [scrollTarget, index, fromPolicy, fieldName]);
    const verifiedMilesLockText = fromPolicy
      ? lockedValuesTooltipTexts.verifiedAnnualMilesPolicy
      : lockedValuesTooltipTexts.verifiedAnnualMilesCarfax;

    const allDeductibleCollisionOptions = useOptions(
      'deductibleCollision',
      state,
      values?.cars?.[index]?.deductibleCollision
    );
    const allDeductibleComprehensiveOptions = useOptions(
      'deductibleComprehensive',
      state,
      values?.cars?.[index]?.deductibleComprehensive
    );
    const { deductibleComprehensiveOptions, deductibleCollisionOptions } = getCoveragesOptions({
      state,
      car,
      policy: policyStore.policy,
      allDeductibleCollisionOptions,
      allDeductibleComprehensiveOptions,
      featureFlags: appConfig?.featureFlags
    });

    const limitRentalOptions = useOptions('limitRental', state, values?.cars?.[index]?.limitRental);
    const limitACPEOptions = useOptions('limitACPE', state, values?.cars?.[index]?.limitACPE);
    // Primary Use is brand-aware: CFM's list (Pleasure / Work < 10 mi / Work >= 10 mi / Business) comes
    // from primaryUseByCarrier, other brands keep the base Pleasure/Business/Commuting list. #35604
    const primaryUseOptions = useOptions('primaryUse', state, values?.cars?.[index]?.primaryUse);
    const ownershipTypeOptions = useOptions('vehicleOwnershipType', state);

    // Transportation Expense (rental) is conditional on Comprehensive for carriers that require it.
    // Comp off → only "No Coverage"; comp on → drop "No Coverage", the $30/900 tier shows as
    // "Included" (base tier, rated as $30) and $60/1800 is selectable. Other brands are untouched.
    // A stated-amount vehicle (GO's StatedAmtInd, imported as statedAmount) never gets it, comp or not,
    // mirroring GO and CFM's renewal normalization. A native antique keeps it. #37129
    const transportationExpenseRequiresComprehensive =
      appConfig?.featureFlags?.transportationExpenseRequiresComprehensive ?? false;
    const currentDeductibleComprehensive = values?.cars?.[index]?.deductibleComprehensive;
    const isStatedAmountVehicle = Number(values?.cars?.[index]?.statedAmount) > 0;
    const transportationExpenseOffered =
      transportationExpenseRequiresComprehensive &&
      !isStatedAmountVehicle &&
      !!currentDeductibleComprehensive &&
      currentDeductibleComprehensive !== 'NONE';
    const limitRentalOptionsForBrand = useMemo(() => {
      if (!transportationExpenseRequiresComprehensive) {
        return limitRentalOptions;
      }
      if (!transportationExpenseOffered) {
        return limitRentalOptions.filter((option) => option.id === 'NONE');
      }
      return limitRentalOptions
        .filter((option) => option.id !== 'NONE')
        .map((option) => (option.id === '30' ? { ...option, value: '$30/day $900 Max (included)' } : option));
    }, [limitRentalOptions, transportationExpenseOffered, transportationExpenseRequiresComprehensive]);

    // Keep the rental selection consistent with Comprehensive and the stated amount (mirrors the
    // coverageLoan reset below).
    const currentLimitRental = values?.cars?.[index]?.limitRental;
    // #37133: a transportation expense GO wrote without Comprehensive stays until Comprehensive changes.
    // Never on a stated-amount vehicle, which GO never gave it (#37129).
    const keepsMigratedLimitRental =
      !isStatedAmountVehicle &&
      !!currentLimitRental &&
      holdsMigratedCarryOverValue(values?.cars?.[index], 'limitRental');
    useEffect(() => {
      if (!transportationExpenseRequiresComprehensive || keepsMigratedLimitRental) {
        return;
      }
      if (transportationExpenseOffered) {
        if (!currentLimitRental || currentLimitRental === 'NONE') {
          setFieldValue(`${fieldName}.limitRental`, '30');
        }
      } else if (currentLimitRental !== 'NONE') {
        setFieldValue(`${fieldName}.limitRental`, 'NONE');
      }
    }, [
      transportationExpenseOffered,
      currentLimitRental,
      keepsMigratedLimitRental,
      fieldName,
      setFieldValue,
      transportationExpenseRequiresComprehensive
    ]);

    // Custom Equipment Coverage (CFM #35599): relabels limitACPE, keeps the dropdown but tolerates
    // migrated values outside the standard tiers by injecting the stored value as a selectable option,
    // and shows a required free-text description whenever a non-zero limit is selected.
    const customEquipmentCoverage = appConfig?.featureFlags?.customEquipmentCoverage ?? false;
    const customEquipmentLabel = (() => {
      if (customEquipmentCoverage) {
        return 'Custom Equipment Coverage';
      }
      return acpeLabels[state] ? `${acpeLabels[state]} COVERAGE` : 'Additional Custom Parts Limit';
    })();
    const currentLimitACPE = values?.cars?.[index]?.limitACPE;
    const limitACPEOptionsForBrand = useMemo(() => {
      if (!customEquipmentCoverage || currentLimitACPE == null || currentLimitACPE === '') {
        return limitACPEOptions;
      }
      if (limitACPEOptions.some((option) => option.id === currentLimitACPE)) {
        return limitACPEOptions;
      }
      const numeric = Number(currentLimitACPE);
      const label = Number.isFinite(numeric) ? `$${numeric.toLocaleString()}` : String(currentLimitACPE);
      return [...limitACPEOptions, { id: currentLimitACPE, value: label }];
    }, [limitACPEOptions, customEquipmentCoverage, currentLimitACPE]);
    const hasCustomEquipment =
      customEquipmentCoverage && !!currentLimitACPE && currentLimitACPE !== 'NONE' && Number(currentLimitACPE) > 0;

    // Clear a stale description when the limit drops back to 0/NONE (mirrors the jointOwner / limitRental
    // resets above) so it isn't persisted with no equipment limit to back it. #35599
    const currentCustomEquipmentDescription = values?.cars?.[index]?.customEquipmentDescription;
    useEffect(() => {
      if (customEquipmentCoverage && !hasCustomEquipment && currentCustomEquipmentDescription) {
        setFieldValue(`${fieldName}.customEquipmentDescription`, '');
      }
    }, [customEquipmentCoverage, hasCustomEquipment, currentCustomEquipmentDescription, fieldName, setFieldValue]);

    // CFM captures per-vehicle sole ownership; when a car is NOT solely owned by the applicant, its
    // joint owner is selected from the policy's *other* drivers — the applicant is filtered out
    // because a vehicle they don't solely own can't name them as its joint owner. The joint-owner
    // fields show only on a No (#36127); any other answer clears a prior selection.
    const supportsVehicleOwnershipDetails = appConfig?.featureFlags?.supportsVehicleOwnershipDetails ?? false;
    const solelyOwnedByApplicant = values?.cars?.[index]?.solelyOwnedByApplicant;
    const currentJointOwner = values?.cars?.[index]?.jointOwner;
    const jointOwnerOptions = useMemo(
      () =>
        (values?.drivers ?? [])
          .filter((driver) => !driver.excludeDriver && !driver.isPrimary)
          .map((driver) => ({ id: driver.id, value: `${driver.firstName ?? ''} ${driver.lastName ?? ''}`.trim() })),
      [values?.drivers]
    );
    useEffect(() => {
      if (supportsVehicleOwnershipDetails && solelyOwnedByApplicant !== false && currentJointOwner) {
        // Empty string (not null) keeps the MUI label shrunk with a blank input, matching the
        // "Relationship" select on the interested-party form; null drops the label into the field.
        setFieldValue(`${fieldName}.jointOwner`, '');
      }
    }, [supportsVehicleOwnershipDetails, solelyOwnedByApplicant, currentJointOwner, fieldName, setFieldValue]);

    // If the selected joint owner is no longer a valid option (driver excluded, removed, or promoted to
    // primary applicant), clear it so a stale, now-invalid driver id can't be submitted. Mirrors the
    // solelyOwnedByApplicant reset above.
    useEffect(() => {
      if (
        supportsVehicleOwnershipDetails &&
        currentJointOwner &&
        !jointOwnerOptions.some((option) => option.id === currentJointOwner)
      ) {
        setFieldValue(`${fieldName}.jointOwner`, '');
      }
    }, [supportsVehicleOwnershipDetails, currentJointOwner, jointOwnerOptions, fieldName, setFieldValue]);

    // Joint Ownership / PA 1200 explicit capture (#35809). Answering "solely owned" Yes clears the
    // whole panel so stale answers can't keep attaching the endorsement.
    const jointOwnerRelated = values?.cars?.[index]?.jointOwnerRelated;
    const hasJointOwnerCapture =
      values?.cars?.[index]?.jointOwnerName ||
      values?.cars?.[index]?.jointOwnerSameHousehold != null ||
      jointOwnerRelated != null ||
      values?.cars?.[index]?.jointOwnerRelationship ||
      values?.cars?.[index]?.jointOwnerAddress;
    useEffect(() => {
      if (supportsVehicleOwnershipDetails && solelyOwnedByApplicant !== false && hasJointOwnerCapture) {
        setFieldValue(`${fieldName}.jointOwnerName`, '');
        setFieldValue(`${fieldName}.jointOwnerSameHousehold`, null);
        setFieldValue(`${fieldName}.jointOwnerRelated`, null);
        setFieldValue(`${fieldName}.jointOwnerRelationship`, '');
        setFieldValue(`${fieldName}.jointOwnerAddress`, null);
      }
    }, [supportsVehicleOwnershipDetails, solelyOwnedByApplicant, hasJointOwnerCapture, fieldName, setFieldValue]);

    // Picking an existing driver prefills the capture: name, the same-household / related answers
    // (derived from the driver's relationshipToInsured), and — when they share the household — the
    // policy address. All of it stays editable; the pick is a convenience, not a binding.
    const handleJointOwnerPicked = (driverId) => {
      const driver = (values?.drivers ?? []).find((d) => d.id === driverId);
      if (!driver) {
        return;
      }
      setFieldValue(`${fieldName}.jointOwnerName`, `${driver.firstName ?? ''} ${driver.lastName ?? ''}`.trim());
      const answers = RELATIONSHIP_TO_JOINT_OWNER_ANSWERS[driver.relationshipToInsured];
      if (answers) {
        setFieldValue(`${fieldName}.jointOwnerSameHousehold`, answers.sameHousehold);
        setFieldValue(`${fieldName}.jointOwnerRelated`, answers.related);
        if (answers.sameHousehold && values?.correctedAddress) {
          const { address, city, state: addrState, zip } = values.correctedAddress;
          setFieldValue(`${fieldName}.jointOwnerAddress`, { address, city, state: addrState, zip });
        }
      } else {
        // Unknown relationship on the picked driver: clear any previous pick's answers rather than
        // carrying them over under the new owner's name (devlo, PR #35863) — staff must answer.
        setFieldValue(`${fieldName}.jointOwnerSameHousehold`, null);
        setFieldValue(`${fieldName}.jointOwnerRelated`, null);
      }
    };

    // A relationship only makes sense for a RELATED joint owner — flipping "Related" back to No
    // must clear it, or a stale "Spouse" is saved alongside related=false (devlo, PR #35863).
    const currentJointOwnerRelationship = values?.cars?.[index]?.jointOwnerRelationship;
    useEffect(() => {
      if (supportsVehicleOwnershipDetails && jointOwnerRelated !== true && currentJointOwnerRelationship) {
        setFieldValue(`${fieldName}.jointOwnerRelationship`, '');
      }
    }, [supportsVehicleOwnershipDetails, jointOwnerRelated, currentJointOwnerRelationship, fieldName, setFieldValue]);

    // Answering "lives in the same household" Yes prefills the policy address (per #35809).
    const handleSameHouseholdChange = (sameHousehold) => {
      if (sameHousehold === true && values?.correctedAddress && !values?.cars?.[index]?.jointOwnerAddress?.address) {
        const { address, city, state: addrState, zip } = values.correctedAddress;
        setFieldValue(`${fieldName}.jointOwnerAddress`, { address, city, state: addrState, zip });
      }
    };

    useEffect(() => {
      if (Object.keys(errors).length) {
        setTouched(errors);
      }
    }, [errors, setTouched]);

    const {
      aebStatus,
      antiTheftStatus,
      blindSpotStatus,
      singleAirbagStatus,
      doubleAirbagStatus,
      deductibleCollision,
      deductibleComprehensive
    } = item;

    // build the header string dynamically
    const getHeaderStatuses = useMemo(() => {
      const buildLabelText = (statuses, type) => {
        const statusLabels = {
          aebStatus: 'Automatic Emergency Braking',
          antiTheftStatus: 'Anti-theft Device',
          blindSpotStatus: 'Blind Spot Warning System',
          singleAirbagStatus: 'Single Airbag',
          doubleAirbagStatus: 'Dual Airbags'
        };

        const statusType =
          type === 'standard' ? vehicleEquipmentStatusCode.STANDARD : vehicleEquipmentStatusCode.OPTIONAL;

        const typeText = type === 'standard' ? 'Equipped with Standard: ' : 'Optionally Equipped with: ';

        return Object.entries(statuses)
          .filter((entry) => String(entry[1]) === String(statusType))
          .reduce((statusText, [status], idx, arr) => {
            const lastItem = idx === arr.length - 1;
            if (!statusText) {
              // Empty accumulator
              return `${statusText} ${typeText} ${statusLabels[status]}${lastItem ? '.' : ''}`;
            }
            if (lastItem) {
              // last standard or optional item
              return `${statusText}, and ${statusLabels[status]}.`;
            }
            return `${statusText}, ${statusLabels[status]}`;
          }, '');
      };

      const statuses = { aebStatus, antiTheftStatus, blindSpotStatus, singleAirbagStatus, doubleAirbagStatus };
      return buildLabelText(statuses, 'standard').concat(buildLabelText(statuses, 'optional'));
    }, [antiTheftStatus, aebStatus, blindSpotStatus, singleAirbagStatus, doubleAirbagStatus]);

    // Rebuilt and Lemon-buyback vehicles (the allowed branded titles) now receive full physical-damage
    // and ancillary coverage by default, just like any other vehicle (#35176), so no coverage field is
    // gated on the title any more. Lease/Loan still requires both deductibles, as it does for every car.
    // #37133: a migrated car that arrived with lease/loan keeps it (and the checkbox, so it can be removed)
    // where the state's EmberTech rules don't offer it (MO non-BIX). Turning it off on load dropped it from
    // the record and the dec on every endorsement. Once a deductible changes it needs both again.
    const formCar = values?.cars?.[index];
    const arrivedWithLeaseLoan = getMigratedArrivedValue(formCar, 'coverageLoan') === true;
    const keepsMigratedLeaseLoan = holdsMigratedCarryOverValue(formCar, 'coverageLoan');
    const showLeaseLoan = displayLeaseLoan || arrivedWithLeaseLoan;
    const leaseLoanAvailable = useMemo(() => {
      // lease loan in all BIX and all GSNIC that isn't our original five
      return (
        showLeaseLoan &&
        ((deductibleCollision !== 'NONE' && deductibleComprehensive !== 'NONE') || keepsMigratedLeaseLoan)
      );
    }, [deductibleCollision, deductibleComprehensive, showLeaseLoan, keepsMigratedLeaseLoan]);

    useEffect(() => {
      if (!leaseLoanAvailable) {
        setFieldValue(`${fieldName}.coverageLoan`, false);
      }
    }, [leaseLoanAvailable, fieldName, setFieldValue]);

    // we now show blind spot in all states so we don't have to keep up with which ones have it as a discount
    // (in response to GH issue 10780)
    const blindSpotAvailable = true;

    // policy-level UMPD should block showing UMPD:
    const showCarLevelUMPD = !policyLevelUMPDStates[state] && !noUMPDStates[state];

    const showIncomeLoss = state === 'VA';

    return (
      <>
        <ConfirmRemoveCarModal
          open={showRemoveModal}
          onClose={() => setShowRemoveModal(false)}
          onRemove={() => onRemove(index, removeFromList)}
          car={item}
        />
        <CollapsibleCard
          content={
            <div className={classes.header}>
              <Label type="infoSubtitle">{getCarHeader(item)}</Label>
              <Label type="infoLabel" className={classes.headerStatus}>
                {getHeaderStatuses}
              </Label>
              {item.brandedTitle && (
                <Label type="infoLabel" className={classes.brandedTitleTag}>
                  Branded title{item.brandedTitleDescription ? `: ${item.brandedTitleDescription}` : ''}
                </Label>
              )}
            </div>
          }
          contentRight={
            hideRemove ? null : (
              <Field
                id={fieldName}
                name={fieldName}
                type="remove"
                disabled={disabled}
                mode="big"
                onClick={() => setShowRemoveModal(true)}
              />
            )
          }
          initialOpen={item.initialOpen}
          // Open this card when the validation-errors modal navigates to a car error (e.g. empty VIN).
          // openSignal is open-only: a changed value opens, never force-closes. Mirrors driver.jsx.
          openSignal={openSignalOnError + openSignalFromNotice}
          collapsibleCardNotification={getCarNotification(item, errorsAndRejections, routesContext, {
            usesRapaSymbols: appConfig?.featureFlags?.autoUsesRapaSymbols ?? false
          })}
        >
          <Form>
            {section !== 'coverage' && (
              <Grid spacing={4} container justifyContent="space-between" className={classes.cardBody}>
                <Field
                  id={`${fieldName}.VIN`}
                  name={`${fieldName}.VIN`}
                  type="string"
                  label="VIN"
                  mode="light"
                  xs={showVerifiedMiles ? 6 : 4}
                />
                <Field
                  id={`${fieldName}.primaryUse`}
                  name={`${fieldName}.primaryUse`}
                  type="select"
                  label="Primary Use"
                  mode="light"
                  xs={showVerifiedMiles ? 6 : 4}
                  options={primaryUseOptions}
                />
                {/* #36726: an existing policy car with no purchase date (migrated) keeps it blank instead of
                  silently taking today's date on mount; a new car on an offer still defaults to today. */}
                <Field
                  id={`${fieldName}.purchaseDate`}
                  name={`${fieldName}.purchaseDate`}
                  type="date"
                  label="Purchase Date"
                  mode="light"
                  xs={showVerifiedMiles ? 6 : 4}
                  defaultToEmpty={routesContext.inPolicyRoute}
                />
                {showVerifiedMiles && (
                  <Grid item xs={6} id={verifiedMilesScrollTargetId(index)}>
                    {lockVerifiedMiles ? (
                      <Field
                        id={`${fieldName}.${lockedMilesField}`}
                        name={`${fieldName}.${lockedMilesField}`}
                        type="numeric"
                        label="Annual Miles"
                        mode="light"
                        format={{ thousandSeparator: true }}
                        readOnly
                        className={classes.readOnlyField}
                        InputProps={{
                          endAdornment: (
                            <Tooltip
                              text={verifiedMilesLockText}
                              placement="top"
                              iconSize="icon16"
                              smallIcon={{ src: icLocked, alt: 'Lock icon' }}
                            />
                          )
                        }}
                      />
                    ) : (
                      <Field
                        id={`${fieldName}.reportedAnnualMiles`}
                        name={`${fieldName}.reportedAnnualMiles`}
                        type="numeric"
                        label="Annual Miles"
                        mode="light"
                        format={{ allowNegative: false, thousandSeparator: true }}
                        disabled={disabled}
                        onChange={(miles) => {
                          // an emptied numeric field yields undefined, which the save drops; null clears it (#34985)
                          if (miles === undefined || miles === '') {
                            setFieldValue(`${fieldName}.reportedAnnualMiles`, null);
                          }
                        }}
                      />
                    )}
                  </Grid>
                )}
                {supportsVehicleOwnershipDetails && (
                  <>
                    {/* #36127: a Yes/No question the agent must answer (a new vehicle starts blank);
                      only a No asks for the joint owner. The long questions sit in a label above
                      the select — as a floating label they overran the field and its neighbour. */}
                    <Grid item xs={6}>
                      <LabelTooltip
                        label="Is the vehicle solely owned by, and registered to, the applicant?"
                        mode="light"
                      >
                        <Field
                          id={`${fieldName}.solelyOwnedByApplicant`}
                          name={`${fieldName}.solelyOwnedByApplicant`}
                          type="select"
                          mode="light"
                          options={YES_NO_OPTIONS}
                          fast={false}
                        />
                      </LabelTooltip>
                    </Grid>
                    {/* #36265: underwriting checks the right interested parties were added (a lessor on
                      a lease, a loss payee on a loan), so each vehicle says how it is owned. Required
                      on new business; printed on the Quote, Application and Dec. */}
                    <Grid item xs={6}>
                      <LabelTooltip label="How is this vehicle owned?" mode="light">
                        <Field
                          id={`${fieldName}.ownership`}
                          name={`${fieldName}.ownership`}
                          type="select"
                          mode="light"
                          options={ownershipTypeOptions}
                          fast={false}
                        />
                      </LabelTooltip>
                    </Grid>
                    {solelyOwnedByApplicant === false && (
                      <>
                        <Field
                          id={`${fieldName}.jointOwner`}
                          name={`${fieldName}.jointOwner`}
                          type="select"
                          label="Joint Owner"
                          mode="light"
                          xs={6}
                          options={jointOwnerOptions}
                          fast={false}
                          onChange={handleJointOwnerPicked}
                        />
                        <Field
                          id={`${fieldName}.jointOwnerName`}
                          name={`${fieldName}.jointOwnerName`}
                          type="string"
                          label="Joint Owner Name"
                          mode="light"
                          xs={6}
                          fast={false}
                        />
                        <Grid item xs={6}>
                          <LabelTooltip label="Lives in the same household as the named insured?" mode="light">
                            <Field
                              id={`${fieldName}.jointOwnerSameHousehold`}
                              name={`${fieldName}.jointOwnerSameHousehold`}
                              type="select"
                              mode="light"
                              options={YES_NO_OPTIONS}
                              fast={false}
                              onChange={handleSameHouseholdChange}
                            />
                          </LabelTooltip>
                        </Grid>
                        <Grid item xs={6}>
                          <LabelTooltip label="Related by blood, marriage or adoption?" mode="light">
                            <Field
                              id={`${fieldName}.jointOwnerRelated`}
                              name={`${fieldName}.jointOwnerRelated`}
                              type="select"
                              mode="light"
                              options={YES_NO_OPTIONS}
                              fast={false}
                            />
                          </LabelTooltip>
                        </Grid>
                        {jointOwnerRelated === true && (
                          <Field
                            id={`${fieldName}.jointOwnerRelationship`}
                            name={`${fieldName}.jointOwnerRelationship`}
                            type="select"
                            label="Relationship"
                            mode="light"
                            xs={6}
                            options={lookupsJson.jointOwnerRelationship}
                            fast={false}
                          />
                        )}
                        <Field
                          id={`${fieldName}.jointOwnerAddress.address`}
                          name={`${fieldName}.jointOwnerAddress.address`}
                          type="string"
                          label="Joint Owner Address"
                          mode="light"
                          xs={8}
                        />
                        <Field
                          id={`${fieldName}.jointOwnerAddress.city`}
                          name={`${fieldName}.jointOwnerAddress.city`}
                          type="string"
                          label="City"
                          mode="light"
                          xs={4}
                        />
                        <Field
                          id={`${fieldName}.jointOwnerAddress.state`}
                          name={`${fieldName}.jointOwnerAddress.state`}
                          type="state"
                          label="State"
                          mode="light"
                          xs={4}
                        />
                        <Field
                          id={`${fieldName}.jointOwnerAddress.zip`}
                          name={`${fieldName}.jointOwnerAddress.zip`}
                          type="string"
                          label="Zip Code"
                          mode="light"
                          xs={4}
                        />
                      </>
                    )}
                  </>
                )}
                <GarageLocation fieldName={fieldName} index={index} />
                {canAddCarsManually && <AddCarDetails fieldName={fieldName} classes={classes} state={state} />}
              </Grid>
            )}
            {section !== 'details' && (
              <Grid spacing={4} container justifyContent="space-between" className={classes.containerDark} key={state}>
                <Grid item xs={4}>
                  <LabelTooltip
                    label={deductibleComprehensiveLabels[state] || 'Comprehensive Deductible'}
                    tooltip={{ label: 'More Info', onHoverText: tooltipHoverTexts.deductibleComprehensive }}
                  >
                    <Field
                      id={`${fieldName}.deductibleComprehensive`}
                      name={`${fieldName}.deductibleComprehensive`}
                      type="select"
                      mode="dark"
                      // how do we change this over to lookupsJson?
                      options={deductibleComprehensiveOptions}
                    />
                  </LabelTooltip>
                </Grid>

                <Grid item xs={4}>
                  <LabelTooltip
                    label="Collision Deductible"
                    tooltip={{ label: 'More Info', onHoverText: tooltipHoverTexts.deductibleCollision(state, true) }}
                  >
                    <Field
                      id={`${fieldName}.deductibleCollision`}
                      name={`${fieldName}.deductibleCollision`}
                      type="select"
                      mode="dark"
                      // how do we change this over to lookupsJson?
                      options={deductibleCollisionOptions}
                    />
                  </LabelTooltip>
                </Grid>

                <Grid item xs={4}>
                  <LabelTooltip
                    label={
                      transportationExpenseRequiresComprehensive
                        ? 'Transportation Expense'
                        : limitRentalLabels[state] || 'Rental Car Expense'
                    }
                    tooltip={{ label: 'More Info', onHoverText: tooltipHoverTexts.limitRental }}
                  >
                    <Field
                      id={`${fieldName}.limitRental`}
                      name={`${fieldName}.limitRental`}
                      type="select"
                      mode="dark"
                      // how do we change this over to lookupsJson?
                      options={limitRentalOptionsForBrand}
                    />
                  </LabelTooltip>
                </Grid>

                {showCarLevelUMPD && (
                  <Grid item xs={6}>
                    <LabelTooltip
                      label="Uninsured Motorist Property Damage Limit"
                      tooltip={{ label: 'More Info', onHoverText: tooltipHoverTexts.limitUMPD }}
                    >
                      <Field
                        id={`${fieldName}.limitUMPD`}
                        name={`${fieldName}.limitUMPD`}
                        type="select"
                        mode="dark"
                        options={limitUMPDOptions}
                      />
                    </LabelTooltip>
                  </Grid>
                )}
                <Grid item xs={6}>
                  <LabelTooltip
                    label={customEquipmentLabel}
                    tooltip={{ label: 'More Info', onHoverText: tooltipHoverTexts.additionalCustomPartsLimit }}
                  >
                    <Field
                      id={`${fieldName}.limitACPE`}
                      name={`${fieldName}.limitACPE`}
                      type="select"
                      mode="dark"
                      xs={12}
                      options={limitACPEOptionsForBrand}
                    />
                  </LabelTooltip>
                </Grid>
                {hasCustomEquipment && (
                  <Field
                    id={`${fieldName}.customEquipmentDescription`}
                    name={`${fieldName}.customEquipmentDescription`}
                    type="string"
                    label="Custom Equipment Description"
                    mode="light"
                    xs={12}
                  />
                )}
                {showIncomeLoss && (
                  <Field
                    id={`${fieldName}.limitIncomeLoss`}
                    name={`${fieldName}.limitIncomeLoss`}
                    type="select"
                    label="Income Loss"
                    mode="dark"
                    xs={6}
                    options={getBrandOptions('limitIncomeLoss', state)}
                    tooltipText={tooltipHoverTexts.incomeLoss}
                    tooltipLabel="More Info"
                  />
                )}

                <Grid item xs={6}>
                  <Field
                    id={`${fieldName}.roadsideAssistance`}
                    name={`${fieldName}.roadsideAssistance`}
                    type="checkbox"
                    mode="dark"
                    label={roadsideLabels[state] || 'With Roadside Assistance Coverage'}
                    tooltipText={tooltipHoverTexts.roadsideAssistance}
                    tooltipLabel="More Info"
                    fast={false}
                  />
                </Grid>

                {/* #36704: Extended Non-Owned is asked per driver now. A car still carrying the old per-car
                  flag shows it here so staff can clear it once the coverage is on the right driver;
                  nothing is migrated automatically, and it keeps rating until it is cleared. */}
                {(appConfig?.featureFlags?.supportsExtendedNonOwnedCoverage ?? false) &&
                  item?.extendedNonOwnedCoverage === true && (
                    <Grid item xs={6}>
                      <Field
                        id={`${fieldName}.extendedNonOwnedCoverage`}
                        name={`${fieldName}.extendedNonOwnedCoverage`}
                        type="checkbox"
                        label="Extended Non-Owned Coverage (now set on a driver: move it to the driver, then untick)"
                        mode="dark"
                        fast={false}
                      />
                    </Grid>
                  )}

                {showRideShare && (
                  <Grid item xs={6}>
                    <Field
                      id={`${fieldName}.rideSharing`}
                      name={`${fieldName}.rideSharing`}
                      type="checkbox"
                      label="With Rideshare Coverage"
                      mode="dark"
                      tooltipText={tooltipHoverTexts.rideSharing}
                      tooltipLabel="More Info"
                      fast={false}
                    />
                  </Grid>
                )}

                {showLeaseLoan && (
                  <Grid item xs={6}>
                    <Field
                      id={`${fieldName}.coverageLoan`}
                      name={`${fieldName}.coverageLoan`}
                      type="checkbox"
                      label="With Lease/Loan Coverage"
                      mode="dark"
                      disabled={!leaseLoanAvailable}
                      fast={false}
                      tooltipText={tooltipHoverTexts.coverageLoan}
                      tooltipLabel="More Info"
                    />
                  </Grid>
                )}

                {aebStatus === vehicleEquipmentStatusCode.OPTIONAL && (
                  <Field
                    id={`${fieldName}.aebConfirmed`}
                    name={`${fieldName}.aebConfirmed`}
                    type="checkbox"
                    label="With factory-installed Automatic Emergency Braking"
                    mode="dark"
                    xs={6}
                  />
                )}
                {antiTheftStatus === vehicleEquipmentStatusCode.OPTIONAL && (
                  <Field
                    id={`${fieldName}.antiTheftConfirmed`}
                    name={`${fieldName}.antiTheftConfirmed`}
                    type="checkbox"
                    label="With factory-installed Anti-Theft Device"
                    mode="dark"
                    xs={6}
                  />
                )}

                {blindSpotAvailable && blindSpotStatus === vehicleEquipmentStatusCode.OPTIONAL && (
                  <Field
                    id={`${fieldName}.blindSpotConfirmed`}
                    name={`${fieldName}.blindSpotConfirmed`}
                    type="checkbox"
                    label="With factory-installed Blind Spot Warning System"
                    mode="dark"
                    xs={6}
                  />
                )}
              </Grid>
            )}
          </Form>
        </CollapsibleCard>
      </>
    );
  }
);

Car.propTypes = {
  section: PropTypes.oneOf(['all', 'details', 'coverage']),
  disabled: PropTypes.bool,
  fieldName: PropTypes.string.isRequired,
  item: PropTypes.shape({
    VIN: PropTypes.string,
    addDate: PropTypes.string,
    bodyType: PropTypes.string,
    confirmed: PropTypes.bool,
    deductibleCollision: PropTypes.string,
    deductibleComprehensive: PropTypes.string,
    coverageLoan: PropTypes.bool,
    extendedNonOwnedCoverage: PropTypes.bool,
    solelyOwnedByApplicant: PropTypes.bool,
    jointOwner: PropTypes.string,
    garageLocation: PropTypes.object,
    limitACPE: PropTypes.string,
    limitRental: PropTypes.string,
    limitUMPD: PropTypes.string,
    make: PropTypes.string,
    model: PropTypes.string,
    primaryUse: PropTypes.string,
    verifiedAnnualMilesDriven: PropTypes.number,
    reportedAnnualMiles: PropTypes.number,
    purchaseDate: PropTypes.string,
    roadsideAssistance: PropTypes.bool,
    year: PropTypes.number,
    aebStatus: PropTypes.oneOf([null, ...Object.values(vehicleEquipmentStatusCode)]),
    antiTheftStatus: PropTypes.oneOf([null, ...Object.values(vehicleEquipmentStatusCode)]),
    singleAirbagStatus: PropTypes.oneOf([null, ...Object.values(vehicleEquipmentStatusCode)]),
    doubleAirbagStatus: PropTypes.oneOf([null, ...Object.values(vehicleEquipmentStatusCode)]),
    aebConfirmed: PropTypes.bool,
    antiTheftConfirmed: PropTypes.bool,
    singleAirbagConfirmed: PropTypes.bool,
    doubleAirbagConfirmed: PropTypes.bool,
    symbolMake: PropTypes.string,
    symbolModel: PropTypes.string,
    symbolStyle: PropTypes.string,
    symbolAux: PropTypes.string,
    isGarageAddressRuleExceptionApproved: PropTypes.bool,
    hasOutstandingRecall: PropTypes.bool
  }).isRequired,
  index: PropTypes.number.isRequired,
  removeFromList: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  // Bumped by form-list when this car is the validation-errors-modal scroll target, to open its card.
  openSignalOnError: PropTypes.number,
  // The condensed Make a change drawer edits one car's deductibles, where removing the car is a different change.
  hideRemove: PropTypes.bool
};

Car.defaultProps = {
  disabled: false,
  openSignalOnError: 0
};

export default withDatePicker(memo(Car));
